import type { Department, VectorStore } from "../types/api";

// Prefix all API calls with the Vite base path so they work under a subpath
// deploy (e.g. /clinical-search/api/...). In dev (base "/") this resolves to "/api/...".
const API_BASE = import.meta.env.BASE_URL.replace(/\/+$/, "");

export async function fetchHealth(): Promise<{
  status: string;
  vector_store: VectorStore;
  default_department?: string;
} | null> {
  try {
    const resp = await fetch(`${API_BASE}/api/health`);
    if (!resp.ok) return null;
    return resp.json();
  } catch {
    return null;
  }
}

export async function fetchDepartments(): Promise<Department[]> {
  try {
    const resp = await fetch(`${API_BASE}/api/departments`);
    if (!resp.ok) return [];
    return resp.json();
  } catch {
    return [];
  }
}

export async function fetchRuns(): Promise<{ runs: import("../types/api").RunSummary[]; count: number }> {
  const resp = await fetch(`${API_BASE}/api/runs`);
  if (!resp.ok) throw new Error("Could not load run history.");
  return resp.json();
}

export async function fetchRun(runId: string): Promise<import("../types/api").SavedRun> {
  const resp = await fetch(`${API_BASE}/api/runs/${encodeURIComponent(runId)}`);
  if (!resp.ok) throw new Error("Could not load this run.");
  return resp.json();
}

export function startQueryStream(
  prompt: string,
  vectorStore: VectorStore,
  departmentId: string,
  onEvent: (event: import("../types/api").SseEvent) => void,
  signal: AbortSignal
): Promise<void> {
  return consumePostSse(
    `${API_BASE}/api/query`,
    { prompt, vector_store: vectorStore, department: departmentId },
    onEvent,
    signal
  );
}

async function consumePostSse(
  url: string,
  body: unknown,
  onEvent: (event: import("../types/api").SseEvent) => void,
  signal: AbortSignal
): Promise<void> {
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`Request failed (${resp.status})`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const chunk of parts) {
      for (const line of chunk.split("\n")) {
        if (!line.startsWith("data:")) continue;
        const raw = line.slice(5).trimStart();
        if (!raw) continue;
        try {
          onEvent(JSON.parse(raw) as import("../types/api").SseEvent);
        } catch {
          onEvent({ type: "message", data: raw });
        }
      }
    }
  }
}
